// Quality tiers (§12.9), initial tier selection and the Auto governor (§24.3). WP7 owns this file.
import type { Settings } from '../profile/settings';
import type { QualityTier } from './Renderer';

export interface TierSpec {
  /** Shadow map size; 0 = no shadow map (blob shadows stay on, §12.3). */
  shadowMapSize: number;
  maxDpr: number;
  /** Multiplies the user's render-scale setting (Potato renders at 0.75). */
  renderScale: number;
  /** Bean lathe radial segments (§12.4: 32 on High, 20 on Low). */
  beanSegments: number;
  particles: number;
  audience: number;
}

export const TIERS: Readonly<Record<QualityTier, Readonly<TierSpec>>> = {
  high: { shadowMapSize: 2048, maxDpr: 2, renderScale: 1, beanSegments: 32, particles: 2000, audience: 120 },
  // §12.4 fixes 32 segments on High and 20 on Low; Medium (200k triangle budget) takes 24.
  medium: { shadowMapSize: 1024, maxDpr: 1.5, renderScale: 1, beanSegments: 24, particles: 1200, audience: 60 },
  low: { shadowMapSize: 0, maxDpr: 1, renderScale: 1, beanSegments: 20, particles: 600, audience: 24 },
  potato: { shadowMapSize: 0, maxDpr: 1, renderScale: 0.75, beanSegments: 20, particles: 300, audience: 0 },
};

export const TIER_ORDER: readonly QualityTier[] = ['high', 'medium', 'low', 'potato'];

/** Device facts used by the Auto choice; read from the browser by `probeDevice()`. */
export interface DeviceHints {
  webdriver: boolean; coarsePointer: boolean; deviceMemoryGb: number | null;
  /**
   * The WebGL renderer string (unmasked when the browser allows it); '' when unknown. Optional so
   * hand-built hints (tests) without it keep the §24.3 webdriver rule.
   */
  gpu?: string;
}

/** Renderer strings of software rasterizers (no GPU): SwiftShader, llvmpipe and friends. */
const SOFTWARE_GL = /swiftshader|llvmpipe|softpipe|software|basic render|mesa offscreen/i;

/** True when `gpu` names a software rasterizer. */
export function isSoftwareGl(gpu: string): boolean {
  return SOFTWARE_GL.test(gpu);
}

/** The WebGL renderer string of a throwaway context ('' when WebGL or the string is unavailable). */
function probeGpu(): string {
  if (typeof document === 'undefined') return '';
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return '';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const name = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return name;
  } catch {
    return '';
  }
}

export function probeDevice(): DeviceHints {
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    webdriver: nav.webdriver === true,
    coarsePointer: typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
    deviceMemoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    gpu: probeGpu(),
  };
}

/**
 * Initial tier (§24.3): `?quality=` param, else the setting if not Auto, else a software rasterizer
 * → potato, else webdriver on an unknown GPU → potato (§24.3's headless rule; a webdriver browser on
 * a real GPU, which the renderer string proves, is treated like any desktop), else a coarse pointer
 * or ≤ 4 GB device memory → low, else high. The governor still steps down if High is too slow.
 */
export function initialTier(param: QualityTier | null, setting: Settings['graphics']['quality'], device: DeviceHints): QualityTier {
  if (param) return param;
  if (setting !== 'auto') return setting;
  const gpu = device.gpu ?? '';
  if (gpu !== '' && isSoftwareGl(gpu)) return 'potato';
  if (device.webdriver && gpu === '') return 'potato';
  if (device.coarsePointer || (device.deviceMemoryGb !== null && device.deviceMemoryGb <= 4)) return 'low';
  return 'high';
}

export function lowerTier(t: QualityTier): QualityTier {
  return TIER_ORDER[Math.min(TIER_ORDER.length - 1, TIER_ORDER.indexOf(t) + 1)];
}

const GOVERNOR = {
  warmupMs: 3000, warmupLimitMs: 22,
  windowMs: 5000, windowLimitMs: 20,
  cooldownMs: 10000,
} as const;

/**
 * Auto-quality governor (§24.3). Fed one frame time per rendered frame while the room is in PLAY.
 * During the first 3 s of PLAY it drops a tier if the average frame exceeds 22 ms; afterwards it
 * drops a tier when a 5 s average exceeds 20 ms, at most once per 10 s. It never raises the tier.
 */
export class QualityGovernor {
  private playMs = 0;
  private windowMs = 0;
  private windowFrames = 0;
  private sinceDropMs = Infinity;
  private warmupDone = false;

  /** Starts a new PLAY period (new round). */
  reset(): void {
    this.playMs = 0;
    this.windowMs = 0;
    this.windowFrames = 0;
    this.warmupDone = false;
  }

  /** Returns true when the tier should drop by one. */
  sample(frameMs: number): boolean {
    this.playMs += frameMs;
    this.sinceDropMs += frameMs;
    this.windowMs += frameMs;
    this.windowFrames++;
    const avg = this.windowMs / this.windowFrames;

    if (!this.warmupDone) {
      if (this.playMs < GOVERNOR.warmupMs) return false;
      this.warmupDone = true;
      return this.endWindow(avg > GOVERNOR.warmupLimitMs);
    }
    if (this.windowMs < GOVERNOR.windowMs) return false;
    return this.endWindow(avg > GOVERNOR.windowLimitMs && this.sinceDropMs >= GOVERNOR.cooldownMs);
  }

  private endWindow(drop: boolean): boolean {
    this.windowMs = 0;
    this.windowFrames = 0;
    if (drop) this.sinceDropMs = 0;
    return drop;
  }
}

export const TIER_LABELS: Readonly<Record<QualityTier, string>> = { high: 'High', medium: 'Medium', low: 'Low', potato: 'Potato' };
